import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';
import apiClient from '../../api/client';
import { Check, X } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { cn } from '../../utils/cn';
import { formatDate, formatEUR } from '../../lib/format';
import { useCapabilitiesStore } from '../../stores/capabilitiesStore';
import { useAuthStore } from '../../stores/authStore';

/**
 * Returns v2 (backend migration 150) — the owner's "MecaRadar Retours" prototype. Shared by the
 * staff screens and the garage portal: the shapes the API returns, who may do what, the status
 * badges, the rules' messages (codes, translated here) and the 3-step tracker.
 */

export type ReturnStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'RECEIVED'
  | 'INSPECTED'
  | 'AWAITING_SUPPLIER'
  | 'CLOSED'
  | 'REFUNDED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';
export const REASONS = [
  'COMMERCIAL',
  'OUR_ERROR',
  'DEFECT_NEW',
  'TRANSPORT',
  'WARRANTY',
  'CORE',
  'RETRACTATION',
] as const;
export const CONDITIONS = ['INTACT', 'OPENED', 'MOUNTED'] as const;
export const FINAL: ReturnStatus[] = ['CLOSED', 'REFUNDED', 'REJECTED', 'CANCELLED', 'EXPIRED'];

export interface RuleMsg {
  code: string;
  params?: Record<string, string | number>;
}

export interface PurchaseView {
  orderLineId: string;
  orderId: string;
  orderNumber: string;
  documents: string[];
  invoiceNumber: string | null;
  deliveredAt: string | null;
  channel: 'WEB' | 'COUNTER' | 'DELIVERY';
  payMethods: string[];
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  qty: number;
  unitHt: number;
  vat: number;
  depositHt: number;
  nonReturnable: boolean;
  electrical: boolean;
  returnedItem: number;
  returnableItem: number;
  returnableCore: number;
  window: 'OPEN' | 'WARRANTY' | 'CLOSED' | 'DONE';
  open: string[];
  returnUntil: string | null;
  retractUntil: string | null;
  warrantyUntil: string | null;
}

export interface PreviewLine {
  orderLineId: string;
  level: 'OK' | 'STUDY' | 'BLOCKED';
  msgs: RuleMsg[];
  legal: string | null;
  feePct: number;
  incomplete: boolean;
  estimateTtc: number;
  itemHt: number;
  feeHt: number;
  depositHt: number;
}
export interface Preview {
  ok: boolean;
  errors: string[];
  lines: PreviewLine[];
  needsStudy: boolean;
  family: string | null;
  totalTtc: number;
}

export interface ReturnRow {
  id: string;
  returnNumber: string;
  status: ReturnStatus;
  legacy: boolean;
  createdAt: string;
  requestedOn: string | null;
  customerId: string | null;
  customerName: string | null;
  customerType: string | null;
  channel: string | null;
  origin: 'COUNTER' | 'PORTAL';
  pickup: 'COUNTER' | 'DRIVER';
  noReceipt: boolean;
  createdBy: string | null;
  rmaUntil: string | null;
  lines: Array<{ sku: string; qty: number; reason: string | null }>;
  totalTtc: number;
  feeTtc: number;
  refundInProgress: boolean;
  cashApprovalRequested: boolean;
  cashApproved: boolean;
}

export interface LineAmounts {
  qty: number;
  itemHt: number;
  feePct: number;
  feeHt: number;
  depositHt: number;
  depreciationHt: number;
  ht: number;
  vat: number;
  vatAmount: number;
  ttc: number;
}
export interface ReturnLine {
  id: string;
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  quantity: number;
  reason: string | null;
  condition: string | null;
  kind: 'ITEM' | 'CORE';
  unitHt: number | null;
  vat: number | null;
  orderLineId: string | null;
  orderNumber: string | null;
  deliveryNotes: string[];
  warranty: {
    plate?: string;
    km?: string | number;
    mountedAt?: string;
    description?: string;
  } | null;
  assessmentLevel: 'OK' | 'STUDY' | null;
  assessmentMsgs: RuleMsg[];
  legalNote: string | null;
  decision: 'ACCEPTED' | 'REFUSED' | null;
  decidedAuto: boolean;
  decidedBy: string | null;
  decisionNote: string | null;
  feeWaivedBy: string | null;
  feeWaivedReason: string | null;
  depreciationHt: number;
  control: {
    receivedQty: number;
    result: string;
    seen: string | null;
    refOk: boolean | null;
    note: string | null;
    by: string | null;
    at: string;
  } | null;
  destination: string | null;
  destinationNote: string | null;
  waitSupplier: boolean;
  supplierOutcome: 'ACCEPTED' | 'REFUSED' | null;
  supplierNote: string | null;
  goodwillBy: string | null;
  goodwillNote: string | null;
  settled: boolean;
  settledHow: string | null;
  settledTtc: number | null;
  legacy: {
    refused: number;
    refusalReason: string | null;
    condition: string | null;
    accepted: number;
    received: number;
  } | null;
  amounts: LineAmounts;
}
export interface TimelineStep {
  date: string;
  at: string;
  code: string;
  params: Record<string, unknown>;
}
export interface Refund {
  id: string;
  channel: string;
  amount: number | string;
  status: string;
  reference: string | null;
}
export interface ReturnDetail {
  id: string;
  returnNumber: string;
  status: ReturnStatus;
  legacy: boolean;
  customerId: string | null;
  customerName: string | null;
  customerType: string | null;
  customerCode: string | null;
  channel: 'B2B' | 'COUNTER' | 'WEB' | null;
  origin: 'COUNTER' | 'PORTAL';
  pickup: 'COUNTER' | 'DRIVER';
  comment: string | null;
  createdAt: string;
  requestedOn: string | null;
  rmaUntil: string | null;
  receivedAt: string | null;
  noReceipt: boolean;
  createdBy: string | null;
  createdById: string | null;
  retractRefundDue: string | null;
  retractShippingProof: string | null;
  retractRefundedOn: string | null;
  shippingRefundTtc: number;
  shippingSettled: boolean;
  cashApprovalRequestedBy: string | null;
  cashApprovedBy: string | null;
  timeline: TimelineStep[];
  orderNumber: string | null;
  rejectionReason: string | null;
  lines: ReturnLine[];
  totals: {
    itemHt: number;
    feeHt: number;
    depositHt: number;
    depreciationHt: number;
    ht: number;
    ttc: number;
    vat: number;
    shippingTtc: number;
    total: number;
  };
  documents: {
    creditNotes: Array<{ id: string; number: string; ttc: number; createdAt: string }>;
    vouchers: Array<{ number: string; ttc: number; balance: number; validUntil: string }>;
    exchanges: Array<{ number: string; createdAt: string }>;
    supplierReturns: Array<{
      id: string;
      number: string;
      status: string;
      supplierName: string;
      shippedAt: string | null;
    }>;
    nextInvoice: { ttc: number; invoiceNumber: string | null } | null;
  };
  refunds: Refund[];
  internal: Array<{ at: string; action: string; by: string | null; role: string | null }>;
}

export interface Policy {
  returnDays: number;
  retractDays: number;
  warrantyMonthsB2B: number;
  warrantyMonthsB2C: number;
  coreDays: number;
  feeIntactPct: number;
  feeOpenedPct: number;
  rmaDays: number;
  cashApprovalAbove: number;
  cashLegalMax: number;
  supplierAnswerDays: number;
  voucherMonths: number;
}

// ---------- who does what (mirrors backend ReturnState.RETURN_ROLES) ----------
const ROLES = {
  counter: ['COUNTER_AGENT', 'POLYVALENT', 'MANAGER', 'OWNER', 'ADMIN'],
  receive: ['COUNTER_AGENT', 'WAREHOUSE_KEEPER', 'POLYVALENT', 'MANAGER', 'OWNER', 'ADMIN'],
  decide: ['MANAGER', 'OWNER'],
  manager: ['MANAGER', 'OWNER', 'ADMIN'],
  supplierAnswer: ['MANAGER', 'OWNER', 'ACCOUNTING', 'PURCHASER', 'ADMIN'],
  ship: ['WAREHOUSE_KEEPER', 'POLYVALENT', 'MANAGER', 'OWNER', 'PURCHASER', 'ADMIN'],
  rules: ['OWNER', 'ADMIN'],
} as const;
export type ReturnAbility = keyof typeof ROLES;

export function useReturnsRole() {
  const role = useCapabilitiesStore((s) => s.data?.role) as string | undefined;
  const userId = useAuthStore((s) => s.user?.sub);
  const portal = !!role && role.startsWith('B2B_');
  const can = (what: ReturnAbility) => !!role && (ROLES[what] as readonly string[]).includes(role);
  return { role, userId, portal, viewer: role === 'B2B_VIEWER', can };
}

/** The base path for links: the staff screens, or the garage portal. */
export const returnsBase = (portal: boolean) => (portal ? '/b2b/returns' : '/returns');

// ---------- labels ----------
const STATUS_TONE: Record<
  ReturnStatus,
  'warning' | 'default' | 'secondary' | 'success' | 'destructive' | 'muted'
> = {
  REQUESTED: 'warning',
  APPROVED: 'default',
  RECEIVED: 'secondary',
  INSPECTED: 'warning',
  AWAITING_SUPPLIER: 'secondary',
  CLOSED: 'success',
  REFUNDED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'muted',
  EXPIRED: 'muted',
};

export function StatusBadge({
  status,
  refundInProgress,
}: {
  status: ReturnStatus;
  refundInProgress?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Badge variant={STATUS_TONE[status] ?? 'muted'} className="whitespace-nowrap">
      {refundInProgress
        ? t('returnsV2.status.REFUND_IN_PROGRESS')
        : t(`returnsV2.status.${status}`)}
    </Badge>
  );
}

export const reasonLabel = (t: TFunction, r: string | null | undefined) =>
  r ? t(`returnsV2.reason.${r}`, { defaultValue: r }) : '—';
export const condLabel = (t: TFunction, c: string | null | undefined) =>
  c ? t(`returnsV2.cond.${c}`, { defaultValue: c }) : '—';

/** A rules message, translated; dates in params are shown the French way. */
export function msgText(t: TFunction, m: RuleMsg): string {
  const p: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(m.params ?? {})) {
    p[k] = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? formatDate(v) : v;
  }
  if (m.code === 'WARRANTY_MISSING' && typeof m.params?.fields === 'string') {
    p.fields = String(m.params.fields)
      .split(',')
      .map((f) => t(`returnsV2.warrantyField.${f}`))
      .join(', ');
  }
  return t(`returnsV2.msg.${m.code}`, { ...p, defaultValue: m.code });
}

export const LEVEL_TONE = { OK: 'success', STUDY: 'warning', BLOCKED: 'destructive' } as const;

/** The verdict of the rules on one line (counter, portal, decision screen). */
export function Verdict({
  level,
  msgs,
  legal,
  incomplete,
  amount,
  detail,
}: {
  level: 'OK' | 'STUDY' | 'BLOCKED';
  msgs: RuleMsg[];
  legal?: string | null;
  incomplete?: boolean;
  amount?: number | null;
  detail?: string | null;
}) {
  const { t } = useTranslation();
  const tone = incomplete ? 'STUDY' : level;
  return (
    <div
      className={cn(
        'mt-2 rounded-md border-l-4 bg-card px-3 py-2 text-sm',
        tone === 'OK' && 'border-l-success',
        tone === 'STUDY' && 'border-l-warning',
        tone === 'BLOCKED' && 'border-l-destructive'
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Badge variant={LEVEL_TONE[tone]}>
          {incomplete ? t('returnsV2.level.INCOMPLETE') : t(`returnsV2.level.${level}`)}
        </Badge>
        {level !== 'BLOCKED' && amount != null && (
          <b className="tabular-nums">{t('returnsV2.ttcAmount', { amount: formatEUR(amount) })}</b>
        )}
        {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
      </div>
      {msgs.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {msgs.map((m, i) => (
            <li key={i}>{msgText(t, m)}</li>
          ))}
        </ul>
      )}
      {legal && <LegalNote code={legal} />}
    </div>
  );
}

export function LegalNote({ code }: { code: string }) {
  const { t } = useTranslation();
  return (
    <p className="mt-2 rounded-md bg-primary-soft px-3 py-1.5 text-xs text-primary">
      {t(`returnsV2.legal.${code}`, { defaultValue: code })}
    </p>
  );
}

/** One timeline entry, translated. */
export function timelineText(t: TFunction, s: TimelineStep): string {
  const p: Record<string, unknown> = { ...s.params };
  for (const [k, v] of Object.entries(p)) {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) p[k] = formatDate(v);
    if (k === 'ttc' && typeof v === 'number') p[k] = formatEUR(v);
  }
  if (s.code === 'CONTROLLED' && Array.isArray(s.params.lines)) {
    p.summary = (s.params.lines as Array<{ sku: string; result: string; note?: string }>)
      .map((l) => `${l.sku} : ${t(`returnsV2.result.${l.result}`)}${l.note ? ` (${l.note})` : ''}`)
      .join(' ; ');
  }
  if (s.code === 'APPROVED')
    p.where = t(`returnsV2.pickup.${String(s.params.pickup ?? 'COUNTER')}`);
  return t(`returnsV2.timeline.${s.code}`, { ...p, defaultValue: s.code });
}

// ---------- the 3-step tracker (prototype: request → on site → quality control) ----------
type StepState = 'done' | 'ko' | 'current' | 'todo';
interface Step {
  key: string;
  state: StepState;
  date: string | null;
  text: string;
  results?: Array<{
    sku: string;
    qty: number;
    verdict: string;
    ok: boolean;
    pending: boolean;
    amount: number;
  }>;
}

export function trackSteps(t: TFunction, r: ReturnDetail): Step[] {
  const refused = r.lines.every((l) => l.decision === 'REFUSED');
  const s1: Step = {
    key: 'REQUEST',
    state: 'done',
    date: r.requestedOn ?? r.createdAt.slice(0, 10),
    text: '',
  };
  if (r.status === 'REQUESTED')
    Object.assign(s1, { state: 'current', text: t('returnsV2.track.studying') });
  else if (r.status === 'REJECTED' && refused)
    Object.assign(s1, {
      state: 'ko',
      text: t('returnsV2.track.refused', { notes: r.rejectionReason ?? '' }),
    });
  else
    s1.text =
      r.origin === 'PORTAL'
        ? t('returnsV2.track.acceptedNumber', { number: r.returnNumber })
        : t('returnsV2.track.recordedCounter');

  const s2: Step = {
    key: 'ON_SITE',
    state: 'todo',
    date: r.receivedAt ? r.receivedAt.slice(0, 10) : null,
    text: '',
  };
  if (r.receivedAt)
    Object.assign(s2, {
      state: 'done',
      text:
        t(r.pickup === 'DRIVER' ? 'returnsV2.track.receivedDriver' : 'returnsV2.track.received') +
        (r.status === 'REJECTED' ? ' ' + t('returnsV2.track.handedBack') : ''),
    });
  else if (r.status === 'APPROVED')
    Object.assign(s2, {
      state: 'current',
      text: t(r.pickup === 'DRIVER' ? 'returnsV2.track.toDriver' : 'returnsV2.track.toCounter', {
        until: formatDate(r.rmaUntil),
        number: r.returnNumber,
      }),
    });
  else if (r.status === 'CANCELLED')
    Object.assign(s2, { state: 'ko', text: t('returnsV2.track.cancelled') });
  else if (r.status === 'EXPIRED')
    Object.assign(s2, {
      state: 'ko',
      text: t('returnsV2.track.expired', { until: formatDate(r.rmaUntil) }),
    });

  const controlled = r.lines.filter((l) => l.control);
  const s3: Step = {
    key: 'CONTROL',
    state: 'todo',
    text: '',
    date: controlled.length
      ? controlled
          .map((l) => l.control!.at)
          .sort()
          .slice(-1)[0]
          .slice(0, 10)
      : null,
    results: r.lines
      .filter((l) => l.decision === 'ACCEPTED')
      .map((l) => {
        const waiting = l.waitSupplier && !l.supplierOutcome && !l.goodwillBy;
        const verdict = !l.control
          ? t('returnsV2.track.pending')
          : waiting
            ? t('returnsV2.track.defectSupplier')
            : l.supplierOutcome === 'REFUSED' && !l.goodwillBy
              ? t('returnsV2.track.supplierRefused', { note: l.supplierNote ?? '' })
              : t(`returnsV2.result.${l.control.result}`) +
                (l.control.note && ['REFUSED', 'CORE_KO'].includes(l.control.result)
                  ? ` : ${l.control.note}`
                  : '');
        return {
          sku: l.sku,
          qty: l.control ? l.control.receivedQty : l.quantity,
          verdict,
          ok: !!l.control && l.amounts.ttc > 0,
          pending: !l.control || waiting,
          amount: l.amounts.ttc,
        };
      }),
  };
  const docs = [
    ...r.documents.creditNotes.map((d) => d.number),
    ...r.documents.vouchers.map((v) => v.number),
    ...r.documents.exchanges.map((x) => x.number),
  ];
  if (r.status === 'CLOSED' || r.status === 'REFUNDED') {
    const tot = r.totals.total;
    Object.assign(
      s3,
      tot > 0
        ? {
            state: 'done',
            text: t(
              r.channel === 'B2B' ? 'returnsV2.track.doneCredited' : 'returnsV2.track.doneRefunded',
              {
                amount: formatEUR(tot),
                docs:
                  docs.join(', ') ||
                  (r.documents.nextInvoice ? t('returnsV2.track.nextInvoice') : ''),
              }
            ),
          }
        : { state: 'ko', text: t('returnsV2.track.doneNothing') }
    );
  } else if (r.status === 'AWAITING_SUPPLIER')
    Object.assign(s3, { state: 'current', text: t('returnsV2.track.atSupplier') });
  else if (r.status === 'INSPECTED')
    Object.assign(s3, { state: 'current', text: t('returnsV2.track.refunding') });
  else if (r.status === 'RECEIVED')
    Object.assign(s3, { state: 'current', text: t('returnsV2.track.controlling') });
  return [s1, s2, s3];
}

export function Track({ r, compact }: { r: ReturnDetail; compact?: boolean }) {
  const { t } = useTranslation();
  const steps = trackSteps(t, r);
  return (
    <ol
      className="grid gap-4 p-4 sm:grid-cols-3"
      aria-label={t('returnsV2.track.aria', { number: r.returnNumber })}
    >
      {steps.map((s, i) => (
        <li key={s.key} className="relative grid grid-cols-[32px_1fr] items-start gap-3">
          {i < steps.length - 1 && (
            <span
              aria-hidden
              className={cn(
                'absolute left-[42px] right-[-10px] top-[15px] hidden h-0.5 sm:block',
                s.state === 'done' ? 'bg-success' : 'bg-border'
              )}
            />
          )}
          <span
            aria-hidden
            className={cn(
              'relative z-10 grid h-8 w-8 place-items-center rounded-full border-2 bg-card text-sm font-bold',
              s.state === 'done' && 'border-success bg-success text-success-foreground',
              s.state === 'ko' && 'border-destructive bg-destructive text-destructive-foreground',
              s.state === 'current' && 'border-primary text-primary ring-4 ring-primary-soft',
              s.state === 'todo' && 'border-border text-muted-foreground'
            )}
          >
            {s.state === 'done' ? (
              <Check className="h-4 w-4" />
            ) : s.state === 'ko' ? (
              <X className="h-4 w-4" />
            ) : (
              i + 1
            )}
          </span>
          <div className="min-w-0">
            <b
              className={cn(
                'relative z-10 block w-max max-w-full bg-card pr-2',
                s.state === 'todo' && 'text-muted-foreground'
              )}
            >
              {t(`returnsV2.track.step.${s.key}`)}
            </b>
            {s.date && s.state !== 'todo' && (
              <span className="block text-xs text-muted-foreground">{formatDate(s.date)}</span>
            )}
            <span className="sr-only">{t(`returnsV2.track.state.${s.state}`)}</span>
            {s.text && (
              <p
                className={cn(
                  'mt-1 text-sm',
                  compact && 'text-xs text-muted-foreground',
                  s.state === 'ko' && 'text-destructive'
                )}
              >
                {s.text}
              </p>
            )}
            {!compact && s.key === 'CONTROL' && s.state !== 'todo' && !!s.results?.length && (
              <ul className="mt-2 grid gap-1 text-sm">
                {s.results.map((x, j) => (
                  <li
                    key={j}
                    className={cn(
                      'border-l-[3px] pl-2',
                      x.pending ? 'border-border' : x.ok ? 'border-success' : 'border-destructive'
                    )}
                  >
                    <span className="font-mono text-xs">{x.sku}</span> × {x.qty} · {x.verdict}
                    {!x.pending && <b className="ml-1 tabular-nums">· {formatEUR(x.amount)}</b>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Filters / panels in the platform's list-page style (Documents.tsx). */
export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-lg border bg-card shadow-sm', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
          {title && <h2 className="flex-1 text-sm font-semibold">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export const thCls =
  'px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground';
export const tdCls = 'px-3 py-2 align-top';
export const cmdHeaders = () => ({ headers: { 'X-Command-Id': crypto.randomUUID() } });

/**
 * A returns refusal in the user's language: the server names the sentence (`details.key`) and
 * its values (`details.params`); a refused request carries the rules' own verdict (`preview`).
 * Anything else falls back to the server's message.
 */
export function returnError(t: TFunction, err: unknown): string {
  const data = (
    err as {
      response?: {
        data?: {
          message?: string;
          details?: { key?: string; params?: Record<string, unknown>; preview?: Preview };
        };
      };
    }
  )?.response?.data;
  const det = data?.details;
  if (det?.key === 'blocked' && det.preview) {
    const pv = det.preview;
    if (pv.errors?.length)
      return t(`returnsV2.previewError.${pv.errors[0]}`, { defaultValue: pv.errors[0] });
    const line = pv.lines.find((l) => l.level === 'BLOCKED');
    if (line?.msgs[0]) return msgText(t, line.msgs[0]);
  }
  if (det?.key) {
    const p: Record<string, unknown> = { ...(det.params ?? {}) };
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) p[k] = formatDate(v);
      if (['cash', 'limit', 'max'].includes(k) && typeof v === 'number' && k !== 'max')
        p[k] = formatEUR(v);
    }
    if (det.key === 'cashLegal' || det.key === 'rulesCash')
      p.max = formatEUR(Number(det.params?.max ?? 0));
    if (typeof p.status === 'string') p.status = t(`returnsV2.status.${p.status}`).toLowerCase();
    if (typeof p.declared === 'string') p.declared = condLabel(t, p.declared);
    if (typeof p.seen === 'string') p.seen = condLabel(t, p.seen);
    return t(`returnsV2.err.${det.key}`, { ...p, defaultValue: data?.message ?? det.key });
  }
  return data?.message ?? (err as Error)?.message ?? String(err);
}

/**
 * The garage area's returns counts (prototype): `toSend` = accepted, the part still to send back
 * (the menu's counter); `open` = every return still in progress (the tracking tab's counter).
 */
export function usePortalReturnCounts(enabled = true): { toSend: number; open: number } {
  const [c, setC] = useState({ toSend: 0, open: 0 });
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return;
    apiClient
      .get<ReturnRow[]>('/returns/board')
      .then((r) =>
        setC({
          toSend: r.data.filter((x) => x.status === 'APPROVED').length,
          open: r.data.filter((x) => !FINAL.includes(x.status)).length,
        })
      )
      .catch(() => undefined);
    // Re-read on navigation: a request just sent or cancelled changes both.
  }, [enabled, pathname]);
  return c;
}

/** The garage area's returns tabs (prototype `portalTabs`): new return · tracking · credits. */
export function PortalReturnTabs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { viewer } = useReturnsRole();
  const { open } = usePortalReturnCounts();
  const tabs = [
    ...(viewer ? [] : [{ to: '/b2b/returns/new', label: t('returnsV2.portal.tabNew') }]),
    { to: '/b2b/returns', label: t('returnsV2.portal.title'), count: open },
    { to: '/b2b/returns/credits', label: t('returnsV2.portal.credits') },
  ];
  const active = (to: string) =>
    to === '/b2b/returns'
      ? pathname === to || /^\/b2b\/returns\/[0-9a-f-]{36}$/i.test(pathname)
      : pathname === to;
  return (
    <div className="-mt-1 mb-4 flex gap-1 overflow-x-auto border-b" role="tablist">
      {tabs.map((x) => (
        <Link
          key={x.to}
          to={x.to}
          role="tab"
          aria-selected={active(x.to)}
          className={cn(
            '-mb-px whitespace-nowrap border-b-[3px] px-3 py-2 text-sm no-underline',
            active(x.to)
              ? 'border-primary font-semibold text-foreground'
              : 'border-transparent text-muted-foreground'
          )}
        >
          {x.label}
          {!!x.count && (
            <span className="ml-1.5 rounded-full bg-destructive px-1.5 text-xs text-destructive-foreground">
              {x.count}
            </span>
          )}
        </Link>
      ))}
    </div>
  );
}

/**
 * A garage account that lands on a staff returns address (typed by hand, an old link) is sent to
 * its own area instead of a staff page it cannot use.
 */
export function useStaffOnly(): string | null {
  const { portal } = useReturnsRole();
  const { pathname } = useLocation();
  if (!portal || !pathname.startsWith('/returns')) return null;
  const m = pathname.match(/^\/returns\/(new|[0-9a-f-]{36})$/i);
  return m
    ? `/b2b/returns/${m[1]}`
    : pathname === '/returns/money'
      ? '/b2b/returns/credits'
      : '/b2b/returns';
}

/** Route guard for the staff returns pages: a garage account goes to its own area. */
export function StaffReturnsRoute({ children }: { children: React.ReactNode }) {
  const to = useStaffOnly();
  return to ? <Navigate to={to} replace /> : <>{children}</>;
}
